/* eslint-disable import/prefer-default-export */
import { BaseTextFieldProps } from '@mui/material';
import { SelectOption, UserRole } from 'remote/global';
import { TFunction } from 'i18next';
import { ControllerFieldState, FieldValues } from 'react-hook-form';
import { UseTranslationResponse } from 'react-i18next';
import { difference, differenceBy, differenceWith, isEqual } from 'lodash-es';

// Changes comma (,) to period (.)
const transformFloatDecimalSeparator = (value: string) => {
  const result = value
    .replace(/[^\d\-.,]/g, '') // Basic sanitization. Allows '-' for negative numbers
    .replace(/,/g, '.') // Change all commas to periods
    .replace(/\.(?=.*\.)/g, ''); // Remove all periods except the last one

  return Number(result);
};

enum DecimalTransformDepthEnum {
  SINGLE = 'SINGLE',
  DOUBLE = 'DOUBLE',
  TRIPLE = 'TRIPLE',
}

const transformToDecimals = (arg: number, depth: DecimalTransformDepthEnum) => {
  const getDepth = () => {
    switch (depth) {
      case DecimalTransformDepthEnum.SINGLE:
        return 10;
      case DecimalTransformDepthEnum.DOUBLE:
        return 100;
      case DecimalTransformDepthEnum.TRIPLE:
        return 1000;
      default:
        return 10;
    }
  };
  const depthNum = getDepth();

  return arg % 1 !== 0 ? Math.trunc(arg * depthNum) / depthNum : arg;
};

const transformToSingleDecimal = (arg: number) =>
  transformToDecimals(arg, DecimalTransformDepthEnum.SINGLE);
const transformToDoubleDecimals = (arg: number) =>
  transformToDecimals(arg, DecimalTransformDepthEnum.DOUBLE);
const transformToTripleDecimals = (arg: number) =>
  transformToDecimals(arg, DecimalTransformDepthEnum.TRIPLE);

const getHelperText = (
  fieldName: string,
  fieldState: ControllerFieldState,
  fieldProps: BaseTextFieldProps | undefined,
  tInstance: UseTranslationResponse<'translation', undefined>
) => {
  const { error } = fieldState ?? {};
  const { helperText } = fieldProps ?? {};
  const { t, i18n } = tInstance;

  if (!error) {
    // If there was a 'helperText' provided manually, try to translate or output
    if (helperText != null) {
      const helperExistsBase = i18n.exists(helperText as string);
      if (helperExistsBase) return t(helperText as string);
      // If a manual translation is not found, just return what was inserted
      return helperText;
    }

    // Syntax is 'FormFields.<fieldName>.helperText'
    const fieldHelperKey = `FormFields.${fieldName}.helperText`;
    const fieldHelperExists = i18n.exists(fieldHelperKey);
    if (fieldHelperExists) return t(fieldHelperKey);

    // If a translation is not found, just return what was inserted
    return helperText;
  }

  if (!error?.message) return undefined;

  // If the error message exists as a translation key, send that instead
  const exists = i18n.exists(error.message);
  if (exists) return t(error.message);

  return error?.message;
};

const createRoleSelectOptions = (t: TFunction): SelectOption[] =>
  Object.keys(UserRole)
    .filter((k) => k.includes('OPERATOR_'))
    .map((k) => ({ label: t(`UserRole.${k}`), value: k }));

function compareFieldArrays<T extends FieldValues>(prev: T[], curr: T[]) {
  const removed = differenceBy(prev, curr, 'id');
  const inserted = differenceBy(curr, prev, 'id');
  // differenceWith gets the change differences, but it also includes the inserted items, so we filter them out
  const changed = difference(differenceWith(curr, prev, isEqual), inserted);

  return { inserted, removed, changed };
}

export {
  transformFloatDecimalSeparator,
  transformToSingleDecimal,
  transformToDoubleDecimals,
  transformToTripleDecimals,
  getHelperText,
  createRoleSelectOptions,
  compareFieldArrays,
};
